import { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
import { syncDateRange } from '../utils/dateUtils';

const LEAD_SOURCES = ['WEBSITE', 'WHATSAPP', 'PHONE', 'EMAIL', 'WALKIN', 'MARKETPLACE', 'EVENT_ORGANIZER', 'OTA', 'SALES_TEAM', 'REFERRAL', 'SOCIAL_MEDIA'];
const EVENT_TYPES = ['WEDDING', 'CORPORATE', 'BIRTHDAY', 'SOCIAL', 'MICE', 'CONFERENCE', 'EXHIBITION', 'RECEPTION', 'ENGAGEMENT', 'OTHER'];
const BUCKET_COLORS = {
  VIP: 'bg-purple-100 text-purple-800 border-purple-300',
  HOT: 'bg-red-100 text-red-800 border-red-300',
  WARM: 'bg-orange-100 text-orange-800 border-orange-300',
  COLD: 'bg-blue-100 text-blue-800 border-blue-300',
  AT_RISK: 'bg-gray-100 text-gray-800 border-gray-300'
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatCurrency = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '-';

function BanquetLeads({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq, onNavigateToDetail, onNavigateToConflicts }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const [leads, setLeads] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Bulk selection
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkStageOpen, setBulkStageOpen] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    pipeline_stage: '',
    lead_bucket: '',
    lead_source: '',
    event_type: '',
    assigned_to: '',
    date_from: '',
    date_to: '',
    sort_by: 'score_overall',
    sort_order: 'DESC',
    search: ''
  });

  // Create/Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [form, setForm] = useState({
    lead_source: 'PHONE',
    lead_source_detail: '',
    client_name: '',
    client_mobile: '',
    client_email: '',
    client_company: '',
    event_type: '',
    event_name: '',
    event_from_date: '',
    event_to_date: '',
    event_start_time: '',
    event_end_time: '',
    expected_pax: '',
    budget_min: '',
    budget_max: '',
    is_room_required: false,
    num_rooms: '',
    food_requirement: '',
    decor_requirement: '',
    special_requests: '',
    notes: ''
  });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadLeads = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, limit: pagination.limit });
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const res = await fetch(`/api/v1/banquet-crm/leads?${params}`, { headers });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Failed to load leads');
      setLeads(data.leads || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
      setSelectedLeads(new Set());
    } catch (err) {
      setError(err?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [headers, filters, pagination.limit]);

  const loadPipelineStages = async () => {
    try {
      const res = await fetch('/api/v1/banquet-crm/pipeline-stages', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setPipelineStages(data.stages || []);
    } catch {}
  };

  const loadUsers = async () => {
    try {
      const res = await fetch('/api/v1/core/users', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setUsers(Array.isArray(data) ? data : []);
    } catch {}
  };

  useEffect(() => {
    if (chainId && propertyId) {
      loadLeads(1);
      loadPipelineStages();
      loadUsers();
    }
  }, [chainId, propertyId]);

  useEffect(() => {
    if (chainId && propertyId) {
      loadLeads(1);
    }
  }, [filters]);

  const handleFilterChange = (field, value) => {
    // syncDateRange: a Created From after the Created To (or vice versa) would
    // leave an inverted range that silently returns no leads — the other end
    // follows instead.
    setFilters(prev => syncDateRange({ ...prev, [field]: value }, 'date_from', 'date_to', field));
  };

  const clearFilters = () => {
    setFilters({
      pipeline_stage: '',
      lead_bucket: '',
      lead_source: '',
      event_type: '',
      assigned_to: '',
      date_from: '',
      date_to: '',
      sort_by: 'score_overall',
      sort_order: 'DESC',
      search: ''
    });
  };

  const openCreateModal = () => {
    setEditingLead(null);
    setForm({
      lead_source: 'PHONE',
      lead_source_detail: '',
      client_name: '',
      client_mobile: '',
      client_email: '',
      client_company: '',
      event_type: '',
      event_name: '',
      event_from_date: '',
      event_to_date: '',
      event_start_time: '',
      event_end_time: '',
      expected_pax: '',
      budget_min: '',
      budget_max: '',
      is_room_required: false,
      num_rooms: '',
      food_requirement: '',
      decor_requirement: '',
      special_requests: '',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setForm({
      lead_source: lead.lead_source || 'PHONE',
      lead_source_detail: lead.lead_source_detail || '',
      client_name: lead.client_name || '',
      client_mobile: lead.client_mobile || '',
      client_email: lead.client_email || '',
      client_company: lead.client_company || '',
      event_type: lead.event_type || '',
      event_name: lead.event_name || '',
      event_from_date: lead.event_from_date ? lead.event_from_date.slice(0, 10) : '',
      event_to_date: lead.event_to_date ? lead.event_to_date.slice(0, 10) : '',
      event_start_time: lead.event_start_time || '',
      event_end_time: lead.event_end_time || '',
      expected_pax: lead.expected_pax || '',
      budget_min: lead.budget_min || '',
      budget_max: lead.budget_max || '',
      is_room_required: lead.is_room_required || false,
      num_rooms: lead.num_rooms || '',
      food_requirement: lead.food_requirement || '',
      decor_requirement: lead.decor_requirement || '',
      special_requests: lead.special_requests || '',
      notes: lead.notes || ''
    });
    setShowModal(true);
  };

  const saveLead = async () => {
    setError('');
    setMessage('');
    if (!form.client_name?.trim()) {
      return setError('Client name is required');
    }

    try {
      const url = editingLead 
        ? `/api/v1/banquet-crm/leads/${editingLead.lead_id}`
        : '/api/v1/banquet-crm/leads';
      const method = editingLead ? 'PUT' : 'POST';

      const body = { ...form };
      if (!body.event_from_date) delete body.event_from_date;
      if (!body.event_to_date) delete body.event_to_date;

      const res = await fetch(url, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Failed to save lead');
      
      setMessage(editingLead ? 'Lead updated' : `Lead created: ${data.lead_no}`);
      setShowModal(false);
      loadLeads(pagination.page);
    } catch (err) {
      setError(err?.message || 'Failed to save lead');
    }
  };

  const changeStage = async (leadId, newStage) => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/stage`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: newStage })
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to change stage');
      }
      loadLeads(pagination.page);
    } catch (err) {
      setError(err?.message || 'Failed to change stage');
    }
  };

  const archiveLead = async (leadId) => {
    if (!window.confirm('Archive this lead?')) return;
    try {
      const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to archive lead');
      }
      setMessage('Lead archived');
      loadLeads(pagination.page);
    } catch (err) {
      setError(err?.message || 'Failed to archive lead');
    }
  };

  // Bulk selection helpers
  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeads.size === leads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(leads.map(l => l.lead_id)));
    }
  };

  // Bulk assign
  const bulkAssign = async (userId) => {
    setBulkProcessing(true);
    setBulkAssignOpen(false);
    setError('');
    let successCount = 0;
    for (const leadId of selectedLeads) {
      try {
        const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/assign`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ assigned_to: userId || null })
        });
        if (res.ok) successCount++;
      } catch {}
    }
    setBulkProcessing(false);
    setMessage(`Assigned ${successCount} of ${selectedLeads.size} leads`);
    setSelectedLeads(new Set());
    loadLeads(pagination.page);
  };

  // Bulk stage change
  const bulkChangeStage = async (stage) => {
    setBulkProcessing(true);
    setBulkStageOpen(false);
    setError('');
    let successCount = 0;
    for (const leadId of selectedLeads) {
      try {
        const res = await fetch(`/api/v1/banquet-crm/leads/${leadId}/stage`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ stage })
        });
        if (res.ok) successCount++;
      } catch {}
    }
    setBulkProcessing(false);
    setMessage(`Changed stage for ${successCount} of ${selectedLeads.size} leads`);
    setSelectedLeads(new Set());
    loadLeads(pagination.page);
  };

  // Export CSV
  const exportCSV = () => {
    const dataToExport = selectedLeads.size > 0
      ? leads.filter(l => selectedLeads.has(l.lead_id))
      : leads;

    if (dataToExport.length === 0) {
      setError('No leads to export');
      return;
    }

    const csvColumns = ['Lead No', 'Client Name', 'Mobile', 'Email', 'Event Type', 'Event Date', 'Pax', 'Budget', 'Score', 'Stage', 'Source', 'Assigned To'];
    const escapeCSV = (val) => {
      const str = val == null ? '' : String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const rows = dataToExport.map(l => [
      l.lead_no || '',
      l.client_name || '',
      l.client_mobile || '',
      l.client_email || '',
      l.event_type || '',
      l.event_from_date ? new Date(l.event_from_date).toLocaleDateString('en-IN') : '',
      l.expected_pax || '',
      l.budget_max || '',
      l.score_overall || 0,
      l.pipeline_stage || '',
      l.lead_source || '',
      l.assigned_to_name || 'Unassigned'
    ].map(escapeCSV).join(','));

    const csv = [csvColumns.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `banquet_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 max-w-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">Banquet Leads</h2>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <span>+ New Lead</span>
        </button>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {message && <div className="mb-3 p-3 bg-green-100 text-green-700 rounded">{message}</div>}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <input
            type="text"
            placeholder="Search name, mobile, email..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="px-3 py-2 border rounded"
          />
          <select
            value={filters.pipeline_stage}
            onChange={(e) => handleFilterChange('pipeline_stage', e.target.value)}
            className="px-3 py-2 border rounded"
          >
            <option value="">All Stages</option>
            {pipelineStages.filter(s => s.is_active).map(s => (
              <option key={s.stage_key} value={s.stage_key}>{s.stage_name}</option>
            ))}
          </select>
          <select
            value={filters.lead_bucket}
            onChange={(e) => handleFilterChange('lead_bucket', e.target.value)}
            className="px-3 py-2 border rounded"
          >
            <option value="">All Buckets</option>
            <option value="VIP">VIP</option>
            <option value="HOT">Hot</option>
            <option value="WARM">Warm</option>
            <option value="COLD">Cold</option>
            <option value="AT_RISK">At Risk</option>
          </select>
          <select
            value={filters.lead_source}
            onChange={(e) => handleFilterChange('lead_source', e.target.value)}
            className="px-3 py-2 border rounded"
          >
            <option value="">All Sources</option>
            {LEAD_SOURCES.map(s => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <select
            value={filters.event_type}
            onChange={(e) => handleFilterChange('event_type', e.target.value)}
            className="px-3 py-2 border rounded"
          >
            <option value="">All Event Types</option>
            {EVENT_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={filters.assigned_to}
            onChange={(e) => handleFilterChange('assigned_to', e.target.value)}
            className="px-3 py-2 border rounded"
          >
            <option value="">All Assignees</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>{u.name_of_user || u.username}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Created From</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="px-3 py-2 border rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Created To</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="px-3 py-2 border rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Sort By</label>
            <select
              value={filters.sort_by}
              onChange={(e) => handleFilterChange('sort_by', e.target.value)}
              className="px-3 py-2 border rounded"
            >
              <option value="score_overall">Score</option>
              <option value="lead_date">Created Date</option>
              <option value="event_from_date">Event Date</option>
              <option value="budget_max">Budget</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Sort Order</label>
            <select
              value={filters.sort_order}
              onChange={(e) => handleFilterChange('sort_order', e.target.value)}
              className="px-3 py-2 border rounded"
            >
              <option value="DESC">High to Low</option>
              <option value="ASC">Low to High</option>
            </select>
          </div>
          <button
            onClick={exportCSV}
            className="px-3 py-2 border rounded text-gray-600 hover:bg-gray-100 self-end"
          >
            Export CSV
          </button>
          <button
            onClick={clearFilters}
            className="px-3 py-2 border rounded text-gray-600 hover:bg-gray-100 self-end"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedLeads.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium text-blue-800">
            {selectedLeads.size} lead{selectedLeads.size > 1 ? 's' : ''} selected
          </span>
          <div className="relative">
            <button
              onClick={() => { setBulkAssignOpen(!bulkAssignOpen); setBulkStageOpen(false); }}
              disabled={bulkProcessing}
              className="px-3 py-1.5 bg-white border border-blue-300 rounded text-sm text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              Assign To
            </button>
            {bulkAssignOpen && (
              <div className="absolute top-full left-0 mt-1 bg-white border rounded shadow-lg z-10 w-48 max-h-60 overflow-y-auto">
                <button
                  onClick={() => bulkAssign(null)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-gray-500"
                >
                  Unassign
                </button>
                {users.map(u => (
                  <button
                    key={u.user_id}
                    onClick={() => bulkAssign(u.user_id)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  >
                    {u.name_of_user || u.username}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => { setBulkStageOpen(!bulkStageOpen); setBulkAssignOpen(false); }}
              disabled={bulkProcessing}
              className="px-3 py-1.5 bg-white border border-blue-300 rounded text-sm text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              Change Stage
            </button>
            {bulkStageOpen && (
              <div className="absolute top-full left-0 mt-1 bg-white border rounded shadow-lg z-10 w-48 max-h-60 overflow-y-auto">
                {pipelineStages.filter(s => s.is_active).map(s => (
                  <button
                    key={s.stage_key}
                    onClick={() => bulkChangeStage(s.stage_key)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  >
                    {s.stage_name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={exportCSV}
            disabled={bulkProcessing}
            className="px-3 py-1.5 bg-white border border-blue-300 rounded text-sm text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          >
            Export CSV
          </button>
          {bulkProcessing && <span className="text-sm text-blue-600">Processing...</span>}
          <button
            onClick={() => setSelectedLeads(new Set())}
            className="ml-auto text-sm text-gray-500 hover:text-gray-700"
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-3 py-3 text-center w-10">
                <input
                  type="checkbox"
                  checked={leads.length > 0 && selectedLeads.size === leads.length}
                  onChange={toggleSelectAll}
                  className="rounded"
                  title="Select All"
                />
              </th>
              <th className="text-left text-xs uppercase">Lead</th>
              <th className="text-left text-xs uppercase">Client</th>
              <th className="text-left text-xs uppercase">Event</th>
              <th className="text-left text-xs uppercase">Date</th>
              <th className="text-left text-xs uppercase">Budget</th>
              <th className="text-left text-xs uppercase">Score</th>
              <th className="text-left text-xs uppercase">Stage</th>
              <th className="text-left text-xs uppercase">Assigned</th>
              <th className="text-left text-xs uppercase">Created</th>
              <th className="text-left text-xs uppercase">Modified</th>
              <th className="text-left text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="12" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan="12" className="px-4 py-8 text-center text-gray-500">No leads found</td></tr>
            ) : leads.map(lead => (
              <tr key={lead.lead_id} className={`hover:bg-gray-50 ${lead.sla_breached ? 'bg-red-50' : ''} ${selectedLeads.has(lead.lead_id) ? 'bg-blue-50' : ''}`}>
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={selectedLeads.has(lead.lead_id)}
                    onChange={() => toggleSelectLead(lead.lead_id)}
                    className="rounded"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-blue-600 flex items-center gap-1 flex-wrap">
                    {lead.lead_no}
                    {lead.external_source === 'EASECRM' && (
                      <span
                        title={`Lead originated in EaseCRM${lead.easecrm_lead_no ? ` (ID: ${lead.easecrm_lead_no})` : lead.easecrm_lead_id ? ` (ID: ${lead.easecrm_lead_id})` : ''}`}
                        className="text-xs font-normal bg-purple-100 text-purple-700 border border-purple-300 rounded px-1.5 py-0.5"
                        style={{ fontSize: '0.65rem', lineHeight: 1.2 }}
                      >
                        🔗 EaseCRM
                      </span>
                    )}
                    {lead.cluster_key && (
                      lead.is_recommended_at_cluster ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onNavigateToConflicts?.(lead.cluster_key); }}
                          title={`AI-Recommended in a competing-leads cluster. Projected revenue: ${formatCurrency(lead.projected_total_revenue || 0)}. Click to view the conflict.`}
                          className="text-xs font-bold border rounded px-1.5 py-0.5 hover:opacity-80 transition"
                          style={{ fontSize: '0.65rem', lineHeight: 1.2,
                                   background: '#dcfce7', color: '#166534', borderColor: '#86efac', cursor: 'pointer' }}
                        >
                          ⭐ AI RECOMMENDED
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onNavigateToConflicts?.(lead.cluster_key); }}
                          title={`Competing for the same hall/date as another lead. Currently ranked #${lead.rank_in_cluster} in this cluster. Click to view.`}
                          className="text-xs font-bold border rounded px-1.5 py-0.5 hover:opacity-80 transition"
                          style={{ fontSize: '0.65rem', lineHeight: 1.2,
                                   background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d', cursor: 'pointer' }}
                        >
                          ⚠ #{lead.rank_in_cluster || '?'} IN CONFLICT
                        </button>
                      )
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{lead.lead_source}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{lead.client_name}</div>
                  <div className="text-xs text-gray-500">{lead.client_mobile || lead.client_email || '-'}</div>
                </td>
                <td className="px-4 py-3">
                  <div>{lead.event_type || '-'}</div>
                  <div className="text-xs text-gray-500">{lead.expected_pax ? `${lead.expected_pax} pax` : '-'}</div>
                </td>
                <td className="px-4 py-3">
                  <div>{formatDate(lead.event_from_date)}{lead.event_to_date && lead.event_to_date !== lead.event_from_date ? ` - ${formatDate(lead.event_to_date)}` : ''}</div>
                  {lead.event_start_time && <div className="text-xs text-gray-500">{lead.event_start_time.slice(0,5)} - {(lead.event_end_time || '').slice(0,5)}</div>}
                </td>
                <td className="px-4 py-3">
                  <div>{formatCurrency(lead.budget_max)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{lead.score_overall || 0}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${BUCKET_COLORS[lead.lead_bucket] || BUCKET_COLORS.COLD}`}>
                      {lead.lead_bucket || 'COLD'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={lead.pipeline_stage}
                    onChange={(e) => changeStage(lead.lead_id, e.target.value)}
                    className="text-xs px-2 py-1 border rounded"
                    style={{ backgroundColor: lead.stage_color ? `${lead.stage_color}20` : '#f3f4f6' }}
                  >
                    {pipelineStages.filter(s => s.is_active).map(s => (
                      <option key={s.stage_key} value={s.stage_key}>{s.stage_name}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-sm">
                  {lead.assigned_to_name || <span className="text-gray-400">Unassigned</span>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                  {lead.created_at && <div className="text-gray-400">{new Date(lead.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {lead.updated_at ? new Date(lead.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                  {lead.updated_at && <div className="text-gray-400">{new Date(lead.updated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onNavigateToDetail?.(lead.lead_id)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                      title="View Details"
                    >
                      View
                    </button>
                    <button
                      onClick={() => openEditModal(lead)}
                      className="text-gray-600 hover:text-gray-800 text-sm"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => archiveLead(lead.lead_id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                      title="Archive"
                    >
                      Archive
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-600">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </div>
          <div className="flex gap-2">
            <button
              disabled={pagination.page <= 1}
              onClick={() => loadLeads(pagination.page - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => loadLeads(pagination.page + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">{editingLead ? 'Edit Lead' : 'New Lead'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Lead Source *</label>
                  <select
                    value={form.lead_source}
                    onChange={(e) => setForm({ ...form, lead_source: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    {LEAD_SOURCES.map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Source Detail</label>
                  <input
                    type="text"
                    value={form.lead_source_detail}
                    onChange={(e) => setForm({ ...form, lead_source_detail: e.target.value })}
                    placeholder="e.g., WeddingBazaar.com"
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Client Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Client Name *</label>
                    <input
                      type="text"
                      value={form.client_name}
                      onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Mobile</label>
                    <input
                      type="text"
                      value={form.client_mobile}
                      onChange={(e) => setForm({ ...form, client_mobile: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={form.client_email}
                      onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Company</label>
                    <input
                      type="text"
                      value={form.client_company}
                      onChange={(e) => setForm({ ...form, client_company: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Event Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Event Type</label>
                    <select
                      value={form.event_type}
                      onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="">Select...</option>
                      {EVENT_TYPES.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Event Name</label>
                    <input
                      type="text"
                      value={form.event_name}
                      onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                      placeholder="e.g., Sharma Wedding"
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Event From Date</label>
                    <input
                      type="date"
                      value={form.event_from_date}
                      onChange={(e) => setForm(syncDateRange({ ...form, event_from_date: e.target.value }, 'event_from_date', 'event_to_date', 'event_from_date'))}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Event To Date</label>
                    <input
                      type="date"
                      value={form.event_to_date}
                      min={form.event_from_date || undefined}
                      onChange={(e) => setForm(syncDateRange({ ...form, event_to_date: e.target.value }, 'event_from_date', 'event_to_date', 'event_to_date'))}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Daily Start Time</label>
                    <input
                      type="time"
                      value={form.event_start_time}
                      onChange={(e) => setForm({ ...form, event_start_time: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Daily End Time</label>
                    <input
                      type="time"
                      value={form.event_end_time}
                      onChange={(e) => setForm({ ...form, event_end_time: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Expected Pax</label>
                    <input
                      type="number"
                      value={form.expected_pax}
                      onChange={(e) => setForm({ ...form, expected_pax: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Budget Min</label>
                      <input
                        type="number"
                        value={form.budget_min}
                        onChange={(e) => setForm({ ...form, budget_min: e.target.value })}
                        className="w-full px-3 py-2 border rounded"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Budget Max</label>
                      <input
                        type="number"
                        value={form.budget_max}
                        onChange={(e) => setForm({ ...form, budget_max: e.target.value })}
                        className="w-full px-3 py-2 border rounded"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Requirements</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_room_required"
                      checked={form.is_room_required}
                      onChange={(e) => setForm({ ...form, is_room_required: e.target.checked })}
                    />
                    <label htmlFor="is_room_required" className="text-sm">Rooms Required</label>
                    {form.is_room_required && (
                      <input
                        type="number"
                        value={form.num_rooms}
                        onChange={(e) => setForm({ ...form, num_rooms: e.target.value })}
                        placeholder="No. of rooms"
                        className="ml-2 w-24 px-2 py-1 border rounded text-sm"
                      />
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">Food Requirement</label>
                  <textarea
                    value={form.food_requirement}
                    onChange={(e) => setForm({ ...form, food_requirement: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">Decor Requirement</label>
                  <textarea
                    value={form.decor_requirement}
                    onChange={(e) => setForm({ ...form, decor_requirement: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">Special Requests</label>
                  <textarea
                    value={form.special_requests}
                    onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">Internal Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={saveLead}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {editingLead ? 'Update Lead' : 'Create Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BanquetLeads;
